import { useEffect, useRef, useCallback } from 'react';

/**
 * Observes a sentinel element and fires onVisible/onHidden when it enters/leaves viewport.
 * Returns a ref to attach to the sentinel element.
 */
export function useIntersectionObserver({ onVisible, onHidden } = {}) {
  const ref = useRef(null);
  const onVisibleRef = useRef(onVisible);
  const onHiddenRef = useRef(onHidden);

  useEffect(() => { onVisibleRef.current = onVisible; }, [onVisible]);
  useEffect(() => { onHiddenRef.current = onHidden; }, [onHidden]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibleRef.current && onVisibleRef.current();
        } else {
          onHiddenRef.current && onHiddenRef.current();
        }
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}
